import React, { useState, useEffect } from "react"
import { cn } from "@/lib/utils"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { AcademicianButton } from "@/components/academician/academician-button"
import { 
  ClockIcon, 
  DownloadIcon, 
  CheckCircleIcon,
  SaveIcon,
  FileTextIcon
} from "lucide-react"

export interface Resource {
  id: string
  name: string
  type: string
  size: string
  url: string
}

export interface QuizQuestion {
  id: string
  question: string
  options: string[]
  correctAnswer: number
}

export interface LearningTabsProps {
  /**
   * Notes content
   */
  notes?: string

  /**
   * Callback when notes change
   */
  onNotesChange?: (notes: string) => void

  /**
   * Current video timestamp for inserting into notes
   */
  currentTimestamp?: string

  /**
   * Quiz questions (if available)
   */
  quiz?: QuizQuestion[]

  /**
   * Downloadable resources
   */
  resources?: Resource[]

  /**
   * Auto-save status
   */
  autoSaveStatus?: "saved" | "saving" | "unsaved"

  /**
   * Additional CSS classes
   */
  className?: string
}

export const LearningTabs: React.FC<LearningTabsProps> = ({
  notes = "",
  onNotesChange,
  currentTimestamp = "00:00",
  quiz,
  resources = [],
  autoSaveStatus = "saved",
  className
}) => {
  const [localNotes, setLocalNotes] = useState(notes)
  const [selectedAnswers, setSelectedAnswers] = useState<Record<string, number>>({})
  const [showResults, setShowResults] = useState(false)

  useEffect(() => {
    setLocalNotes(notes)
  }, [notes])

  const handleNotesChange = (value: string) => {
    setLocalNotes(value)
    onNotesChange?.(value)
  }

  const handleInsertTimestamp = () => {
    const newNotes = localNotes + `\n[${currentTimestamp}] `
    setLocalNotes(newNotes)
    onNotesChange?.(newNotes)
  }

  const handleAnswerSelect = (questionId: string, answerIndex: number) => {
    setSelectedAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }))
  }

  const handleSubmitQuiz = () => {
    setShowResults(true)
  }

  const getQuizScore = () => {
    if (!quiz) return 0
    let correct = 0
    quiz.forEach(q => {
      if (selectedAnswers[q.id] === q.correctAnswer) {
        correct++
      }
    })
    return Math.round((correct / quiz.length) * 100)
  }

  const getAutoSaveIndicator = () => {
    switch (autoSaveStatus) {
      case "saved":
        return (
          <span className="text-xs text-green-600 dark:text-green-500 flex items-center gap-1">
            <CheckCircleIcon className="w-3 h-3" />
            Saved
          </span>
        )
      case "saving":
        return (
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <SaveIcon className="w-3 h-3 animate-pulse" />
            Saving...
          </span>
        )
      case "unsaved":
        return (
          <span className="text-xs text-amber-600 dark:text-amber-500">
            Unsaved changes
          </span>
        )
    }
  }

  return (
    <div className={cn("h-full", className)}>
      <Tabs defaultValue="notes" className="h-full flex flex-col">
        <TabsList className="w-full grid grid-cols-3">
          <TabsTrigger value="notes">Notes</TabsTrigger>
          <TabsTrigger value="quiz">
            Quiz
            {quiz && quiz.length > 0 && (
              <span className="ml-1 text-xs">({quiz.length})</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="resources">
            Resources
            {resources.length > 0 && (
              <span className="ml-1 text-xs">({resources.length})</span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Notes Tab */}
        <TabsContent value="notes" className="flex-1 space-y-4 mt-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Your Notes</h3>
            {getAutoSaveIndicator()}
          </div>

          <Textarea
            value={localNotes}
            onChange={(e) => handleNotesChange(e.target.value)}
            placeholder="Take notes while watching the video..."
            className="min-h-[300px] resize-none"
          />

          <Button
            variant="outline"
            size="sm"
            leftIcon={<ClockIcon className="w-4 h-4" />}
            onClick={handleInsertTimestamp}
            className="w-full"
          >
            Insert Timestamp ({currentTimestamp})
          </Button>
        </TabsContent>

        {/* Quiz Tab */}
        <TabsContent value="quiz" className="flex-1 space-y-4 mt-4">
          {quiz && quiz.length > 0 ? (
            <>
              <div className="space-y-6">
                {quiz.map((question, qIndex) => (
                  <div key={question.id} className="space-y-3">
                    <h4 className="text-sm font-semibold text-foreground">
                      {qIndex + 1}. {question.question}
                    </h4>
                    <div className="space-y-2">
                      {question.options.map((option, oIndex) => {
                        const isSelected = selectedAnswers[question.id] === oIndex
                        const isCorrect = question.correctAnswer === oIndex
                        const showCorrect = showResults && isCorrect
                        const showIncorrect = showResults && isSelected && !isCorrect

                        return (
                          <button
                            key={oIndex}
                            onClick={() => handleAnswerSelect(question.id, oIndex)}
                            disabled={showResults}
                            className={cn(
                              "w-full text-left px-4 py-3 rounded-lg border-2 transition-colors",
                              "hover:border-primary/50",
                              isSelected && !showResults && "border-primary bg-primary/5",
                              !isSelected && !showResults && "border-border",
                              showCorrect && "border-green-600 bg-green-600/10 dark:border-green-500 dark:bg-green-500/10",
                              showIncorrect && "border-red-600 bg-red-600/10 dark:border-red-500 dark:bg-red-500/10",
                              showResults && !isSelected && !isCorrect && "opacity-50"
                            )}
                          >
                            <span className="text-sm text-foreground">{option}</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {!showResults ? (
                <AcademicianButton
                  variant="primary"
                  onClick={handleSubmitQuiz}
                  disabled={Object.keys(selectedAnswers).length !== quiz.length}
                  className="w-full"
                >
                  Submit Quiz
                </AcademicianButton>
              ) : (
                <div className="bg-primary/10 border border-primary rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-foreground mb-1">
                    {getQuizScore()}%
                  </div>
                  <div className="text-sm text-muted-foreground">
                    You got {Object.values(selectedAnswers).filter((ans, idx) => ans === quiz[idx]?.correctAnswer).length} out of {quiz.length} correct
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <FileTextIcon className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground mb-1">No Quiz Available</p>
              <p className="text-xs text-muted-foreground">
                Complete the video to unlock the quiz
              </p>
            </div>
          )}
        </TabsContent>

        {/* Resources Tab */}
        <TabsContent value="resources" className="flex-1 space-y-4 mt-4">
          {resources.length > 0 ? (
            <div className="space-y-2">
              {resources.map((resource) => (
                <div
                  key={resource.id}
                  className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-foreground truncate">
                      {resource.name}
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      {resource.type} • {resource.size}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 ml-2"
                    onClick={() => window.open(resource.url, "_blank")}
                  >
                    <DownloadIcon className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <DownloadIcon className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-foreground mb-1">No Resources Available</p>
              <p className="text-xs text-muted-foreground">
                Resources will appear here when available
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}